import React, { useCallback, useEffect, useState } from 'react';
import {
    Button, Card, Descriptions, Drawer, Form, Input, InputNumber, Modal, Popconfirm, Progress, Radio, Select, Space, Spin,
    Statistic, Row, Col, Switch, Table, Tag, Tooltip, Typography, message,
} from 'antd';
import { LockOutlined, PlusOutlined, ReloadOutlined } from '@ant-design/icons';
import tmAdminService from '../../services/tmService';
import tmAdminExtService from '../../services/tmAdminExtService';
import StreamOpsPanel from './StreamOpsPanel';
import { STREAM_STATUS_OPTIONS, StreamStatusTag, TypeTag, fmtNum, fmtTime, ok, toastErr } from './common';

const { Text } = Typography;

const askReason = (title, defaultReason, onOk) => {
    let value = '';
    Modal.confirm({
        title,
        content: <Input.TextArea rows={2} maxLength={200} placeholder={defaultReason} onChange={(e) => { value = e.target.value; }} />,
        okButtonProps: { danger: true },
        onOk: () => onOk(value.trim() || defaultReason),
    });
};

const rate = (n, d) => (d ? `${((n / d) * 100).toFixed(2)}%` : '-');

const LEVEL_COLOR = ['red', 'default', 'blue', 'cyan', 'green', 'gold', 'purple'];
const LevelTag = ({ trust }) => (trust ? (
    <Tag color={LEVEL_COLOR[trust.level] || 'default'} style={{ marginInlineEnd: 0 }}>
        Lv{trust.level}{trust.locked ? <LockOutlined style={{ marginInlineStart: 4 }} /> : null}
    </Tag>
) : '-');

/** 账号信用等级：查看 + 设定/锁定（作用于该用户全部发信流；内部套餐不受影响） */
const TrustPanel = ({ userId, trust, onChanged }) => {
    const [level, setLevel] = useState(trust?.level ?? 1);
    const [locked, setLocked] = useState(!!trust?.locked);
    const [reason, setReason] = useState('');
    const [busy, setBusy] = useState(false);
    useEffect(() => { setLevel(trust?.level ?? 1); setLocked(!!trust?.locked); }, [trust]);
    if (!trust) return null;
    const save = async () => {
        setBusy(true);
        try {
            const r = await tmAdminService.setTrust(userId, { level, locked, reason: reason.trim() || '管理员设定' });
            if (ok(r)) { message.success('已更新信用等级'); setReason(''); onChanged(); }
        } catch (e) {
            toastErr(e, '设置失败');
        } finally {
            setBusy(false);
        }
    };
    const next = trust.next_level;
    return (
        <Card size="small" title={<Space>账号信用等级 <LevelTag trust={trust} /></Space>}
            extra={<Text type="secondary" style={{ fontSize: 12 }}>免费版 {fmtNum(trust.allowance_month)} 封/月 · {trust.hourly_free}/时 · 付费 ×{trust.hourly_factor}</Text>}>
            <Space wrap>
                <Select style={{ width: 110 }} value={level} onChange={setLevel}
                    options={(trust.levels || [0, 1, 2, 3, 4, 5, 6].map((l) => ({ level: l }))).map((l) => ({ value: l.level, label: `Lv${l.level}` }))} />
                <Tooltip title="锁定后不自动升降"><Space size={4}><Switch size="small" checked={locked} onChange={setLocked} />锁定</Space></Tooltip>
                <Input style={{ width: 220 }} maxLength={200} placeholder="原因" value={reason} onChange={(e) => setReason(e.target.value)} />
                <Button type="primary" loading={busy} onClick={save}>保存</Button>
            </Space>
            {next && (
                <div style={{ marginTop: 8, fontSize: 12 }}>
                    <Text type="secondary">升 Lv{next.level}：</Text>
                    {(next.requirements || []).map((r) => (
                        <Tag key={r.key} color={r.met ? 'green' : 'default'} style={{ marginTop: 4 }}>{r.met ? '✓' : '✗'} {r.label}</Tag>
                    ))}
                    {trust.promotion_blocked && <Text type="secondary">（自动升级暂停：{({ locked: '已锁定', cooldown: '降级冷却', level_0: 'Lv0 仅管理员恢复', max: '已满级' })[trust.promotion_blocked]}）</Text>}
                </div>
            )}
            {(trust.history || []).length > 0 && (
                <div style={{ marginTop: 8 }}>
                    {(trust.history || []).slice(0, 5).map((h, i) => (
                        <div key={i} style={{ fontSize: 12 }}>
                            <Text type="secondary">{fmtTime(h.at)}</Text> Lv{h.from} → Lv{h.to}{h.by_admin ? '（管理员）' : ''} · {h.reason}
                        </div>
                    ))}
                </div>
            )}
        </Card>
    );
};

const StreamDrawer = ({ id, groups, onClose, onChanged }) => {
    const [s, setS] = useState(null);
    const [busy, setBusy] = useState(false);

    const load = useCallback(async () => {
        if (!id) return;
        try {
            const r = await tmAdminService.getStream(id);
            setS(r?.data || null);
        } catch (e) {
            toastErr(e, '加载失败');
        }
    }, [id]);

    useEffect(() => { setS(null); load(); }, [load]);

    const act = async (fn, okText) => {
        setBusy(true);
        try {
            const r = await fn();
            if (ok(r)) {
                message.success(r.message && r.message !== 'success' ? r.message : okText);
                load();
                onChanged();
            }
        } catch (e) {
            toastErr(e, '操作失败');
        } finally {
            setBusy(false);
        }
    };

    const risk = s?.risk || {};
    const sent = risk.sent_24h || 0;

    return (
        <Drawer open={!!id} onClose={onClose} width={720} title={s ? `#${s.id} ${s.name}` : ''} destroyOnClose>
            {!s ? <div style={{ textAlign: 'center', padding: 32 }}><Spin /></div> : (
                <Space direction="vertical" size={16} style={{ width: '100%' }}>
                    <Space wrap>
                        <TypeTag type={s.type} />
                        <StreamStatusTag stream={s} />
                        {s.sandbox && <Tag color="gold">沙箱</Tag>}
                    </Space>
                    <Space wrap>
                        {s.status === 'active' && <Button loading={busy} onClick={() => askReason('暂停发信流', '管理员暂停', (r) => act(() => tmAdminExtService.pause(s.id, r), '已暂停'))}>暂停</Button>}
                        {s.status === 'paused' && <Button loading={busy} onClick={() => act(() => tmAdminExtService.resume(s.id), '已恢复')}>恢复</Button>}
                        {s.status !== 'banned'
                            ? <Button danger loading={busy} onClick={() => askReason('封禁发信流（吊销全部 API 密钥）', '管理员封禁', (r) => act(() => tmAdminExtService.ban(s.id, r), '已封禁'))}>封禁</Button>
                            : <Popconfirm title="解封后用户需重新创建 API 密钥" onConfirm={() => act(() => tmAdminExtService.unban(s.id), '已解封')}><Button loading={busy}>解封</Button></Popconfirm>}
                        {s.sandbox && <Popconfirm title="解除沙箱？" onConfirm={() => act(() => tmAdminExtService.sandbox(s.id, false, '管理员解除沙箱'), '已解除沙箱')}><Button loading={busy}>解除沙箱</Button></Popconfirm>}
                        <Select style={{ width: 220 }} value={s.group_override ?? 0} disabled={busy}
                            onChange={(v) => act(() => tmAdminService.setStreamGroup(s.id, v || null), '已切换分组')}
                            options={[{ value: 0, label: `跟随套餐（#${s.group_id}）` }, ...groups.map((g) => ({ value: g.id, label: `${g.name} · ${g.smtp_host}` }))]} />
                    </Space>
                    <TrustPanel userId={s.user_id} trust={s.owner_trust} onChanged={() => { load(); onChanged(); }} />
                    <Card size="small" title="24 小时风控">
                        <Row gutter={8}>
                            <Col span={6}><Statistic title="发送" value={sent} valueStyle={{ fontSize: 18 }} /></Col>
                            <Col span={6}><Statistic title={`硬退 ${rate(risk.hard_bounces_24h, sent)}`} value={risk.hard_bounces_24h || 0} valueStyle={{ fontSize: 18, color: sent >= 200 && risk.hard_bounces_24h / sent > 0.05 ? '#cf1322' : undefined }} /></Col>
                            <Col span={6}><Statistic title={`投诉 ${rate(risk.complaints_24h, sent)}`} value={risk.complaints_24h || 0} valueStyle={{ fontSize: 18, color: sent >= 200 && risk.complaints_24h / sent > 0.001 ? '#cf1322' : undefined }} /></Col>
                            <Col span={6}><Statistic title="垃圾拒收" value={risk.spam_rejected_24h || 0} valueStyle={{ fontSize: 18, color: risk.spam_rejected_24h >= 5 ? '#cf1322' : undefined }} /></Col>
                        </Row>
                        {(risk.virus_events || []).length > 0 && (
                            <div style={{ marginTop: 8 }}>
                                <Text type="danger">病毒事件：</Text>
                                {(risk.virus_events || []).map((v, i) => {
                                    let d = v.data;
                                    try { d = typeof d === 'string' ? JSON.parse(d) : d; } catch (_) { /* keep raw */ }
                                    return <div key={i} style={{ fontSize: 12 }}>{fmtTime(v.at)} · {d?.virus_name || JSON.stringify(d)}</div>;
                                })}
                            </div>
                        )}
                    </Card>
                    <Descriptions column={2} size="small" bordered>
                        <Descriptions.Item label="用户">#{s.user_id}</Descriptions.Item>
                        <Descriptions.Item label="套餐">{s.plan?.name || '-'}</Descriptions.Item>
                        <Descriptions.Item label="订阅">#{s.subscription_id} {s.subscription_status}</Descriptions.Item>
                        <Descriptions.Item label="到期">{fmtTime(s.expiry_date)}</Descriptions.Item>
                        <Descriptions.Item label="SMTP 主机">{s.smtp_host}</Descriptions.Item>
                        <Descriptions.Item label="分组">#{s.group_id}{s.group_override ? '（覆盖）' : ''}</Descriptions.Item>
                        <Descriptions.Item label="本月用量">{fmtNum(s.usage?.monthly_used)} / {s.usage?.monthly_limit === -1 ? '不限' : fmtNum(s.usage?.monthly_limit)}</Descriptions.Item>
                        <Descriptions.Item label="本小时剩余">{s.usage?.hourly_remaining === -1 ? '不限' : fmtNum(s.usage?.hourly_remaining)} / {fmtNum(s.limits?.hourly)}</Descriptions.Item>
                        <Descriptions.Item label="API 密钥">{s.api_keys}</Descriptions.Item>
                        <Descriptions.Item label="实体地址">{s.postal_address || '-'}</Descriptions.Item>
                        <Descriptions.Item label="暂停于">{fmtTime(s.paused_at)}</Descriptions.Item>
                        <Descriptions.Item label="封禁于">{fmtTime(s.banned_at)}</Descriptions.Item>
                    </Descriptions>
                    <Table size="small" rowKey="id" pagination={false} dataSource={s.domains || []} columns={[
                        { title: '域名', dataIndex: 'domain' },
                        { title: '范围', dataIndex: 'stream_id', render: (v) => (v ? '本流' : '共用') },
                        { title: '验证', dataIndex: 'verified_at', render: (v) => (v ? <Tag color="green">已验证</Tag> : <Tag>未验证</Tag>) },
                        { title: 'DKIM', dataIndex: 'dkim_ok', render: (v) => (v ? '✓' : '-') },
                        { title: 'SPF', dataIndex: 'spf_ok', render: (v) => (v ? '✓' : '-') },
                    ]} />
                    <StreamOpsPanel streamId={s.id} onChanged={() => { load(); onChanged(); }} />
                </Space>
            )}
        </Drawer>
    );
};

const Streams = () => {
    const [rows, setRows] = useState([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(false);
    const [page, setPage] = useState({ current: 1, pageSize: 50 });
    const [f, setF] = useState({});
    const [groups, setGroups] = useState([]);
    const [openId, setOpenId] = useState(null);
    const [creating, setCreating] = useState(false);
    const [saving, setSaving] = useState(false);
    const [form] = Form.useForm();

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const r = await tmAdminService.listStreams({ ...f, page: page.current, per_page: page.pageSize });
            setRows(r?.data?.items || []);
            setTotal(r?.data?.total || 0);
        } catch (e) {
            toastErr(e, '加载失败');
        } finally {
            setLoading(false);
        }
    }, [f, page]);

    useEffect(() => { load(); }, [load]);
    useEffect(() => {
        tmAdminService.listGroups().then((r) => setGroups(Array.isArray(r?.data) ? r.data : [])).catch(() => {});
    }, []);

    const setFilter = (patch) => { setF((x) => ({ ...x, ...patch })); setPage((p) => ({ ...p, current: 1 })); };

    const createInternal = async () => {
        const v = await form.validateFields();
        setSaving(true);
        try {
            const r = await tmAdminService.createInternalStream(v);
            if (ok(r)) { message.success('已创建'); setCreating(false); form.resetFields(); load(); }
        } catch (e) {
            toastErr(e, '创建失败');
        } finally {
            setSaving(false);
        }
    };

    const columns = [
        { title: 'ID', dataIndex: 'id', width: 70 },
        { title: '名称', dataIndex: 'name', render: (v, s) => <Typography.Link onClick={() => setOpenId(s.id)}>{v}</Typography.Link> },
        { title: '用户', key: 'user', render: (_, s) => <span>#{s.user?.id} {s.user?.username}<br /><Text type="secondary" style={{ fontSize: 12 }}>{s.user?.email}</Text></span> },
        { title: '类型', dataIndex: 'type', width: 70, render: (v) => <TypeTag type={v} /> },
        { title: '等级', key: 'trust', width: 70, render: (_, s) => <LevelTag trust={s.owner_trust} /> },
        {
            title: '状态', key: 'status', width: 130,
            render: (_, s) => <Space size={4}><StreamStatusTag stream={s} />{s.sandbox && <Tag color="gold" style={{ marginInlineEnd: 0 }}>沙箱</Tag>}</Space>,
        },
        { title: '套餐', key: 'plan', render: (_, s) => s.plan?.name || '-' },
        { title: '分组', key: 'group', width: 90, render: (_, s) => `#${s.group_id}${s.group_override ? '*' : ''}` },
        { title: '24h', dataIndex: 'sent_24h', width: 80, render: fmtNum },
        {
            title: '本月', key: 'usage', width: 160,
            render: (_, s) => {
                const u = s.usage || {};
                const pct = u.monthly_limit > 0 ? Math.min(100, Math.round((u.monthly_used / u.monthly_limit) * 100)) : 0;
                return <div><Text style={{ fontSize: 12 }}>{fmtNum(u.monthly_used)} / {u.monthly_limit === -1 ? '不限' : fmtNum(u.monthly_limit)}</Text><Progress percent={pct} size="small" showInfo={false} /></div>;
            },
        },
        { title: '创建', dataIndex: 'created_at', width: 160, render: fmtTime },
    ];

    return (
        <Card title="发信流" extra={
            <Space>
                <Button icon={<ReloadOutlined />} onClick={load} />
                <Button type="primary" icon={<PlusOutlined />} onClick={() => setCreating(true)}>内部发信流</Button>
            </Space>
        }>
            <Space wrap style={{ marginBottom: 12 }}>
                <Input.Search allowClear placeholder="名称 / 用户名 / 邮箱" style={{ width: 240 }} onSearch={(v) => setFilter({ q: v.trim() || undefined })} />
                <InputNumber placeholder="用户 ID" style={{ width: 120 }} min={1} onChange={(v) => setFilter({ user_id: v || undefined })} />
                <Select allowClear placeholder="状态" style={{ width: 120 }} options={STREAM_STATUS_OPTIONS} onChange={(v) => setFilter({ status: v })} />
                <Select allowClear placeholder="沙箱" style={{ width: 110 }} options={[{ value: 1, label: '沙箱中' }, { value: 0, label: '已解除' }]} onChange={(v) => setFilter({ sandbox: v })} />
            </Space>
            <Table rowKey="id" size="small" loading={loading} dataSource={rows} columns={columns} scroll={{ x: 1100 }}
                pagination={{ ...page, total, showSizeChanger: true, onChange: (current, pageSize) => setPage({ current, pageSize }) }} />
            <StreamDrawer id={openId} groups={groups} onClose={() => setOpenId(null)} onChanged={load} />
            <Modal open={creating} title="创建内部发信流" onOk={createInternal} confirmLoading={saving} onCancel={() => setCreating(false)} destroyOnClose>
                <Form form={form} layout="vertical" initialValues={{ type: 'transactional' }}>
                    <Form.Item name="user_id" label="归属用户 ID" rules={[{ required: true }]}><InputNumber min={1} style={{ width: '100%' }} /></Form.Item>
                    <Form.Item name="name" label="名称" rules={[{ required: true }]}><Input maxLength={64} /></Form.Item>
                    <Form.Item name="type" label="类型">
                        <Radio.Group><Radio value="transactional">交易</Radio><Radio value="marketing">营销</Radio></Radio.Group>
                    </Form.Item>
                </Form>
            </Modal>
        </Card>
    );
};

export default Streams;
